import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { Box, Typography, TextField, Button, MenuItem, List, ListItem, ListItemText, Paper, Avatar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { API_URL } from '../apiConfig';


const communicationTypes = [
  { value: 'support', label: 'Support' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'inquiry', label: 'General Inquiry' },
];

const styles = {
  container: {
    margin: '50px',
    display: 'flex',
    height: 'calc(96.5vh - 100px)', // Adjust the height to fit the viewport minus margins
  },
  sidebar: {
    width: '300px',
    borderRight: '1px solid var(--divider)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    height: '100%', // Ensure sidebar takes full height of container
    backgroundColor: '#f7f7f7', // Light gray background color for the sidebar
    padding: '16px',
    boxShadow: '2px 0 5px rgba(0, 0, 0, 0.1)', // Add subtle shadow for depth
  },
  sidebarHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sidebarLogo: {
    marginRight: '8px',
  },
  sidebarThreadAvatar: {
    width: '40px',
    height: '40px',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#fff',
    backgroundColor: (theme) => theme.palette.primary.main,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    border: '2px solid #fff',
    marginLeft: '-10px', // This moves the avatar 10 pixels to the left
    marginRight: '10px', // This maintains the original spacing on the right
  },
  sidebarTitle: {
    fontWeight: 'bold',
    fontSize: '1.2em',
  },
  sidebarButton: {
    marginBottom: '16px',
    backgroundColor: 'var(--primary-main)',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'var(--primary-dark)',
    },
  },
  list: {
    overflowY: 'auto',
    flexGrow: 1,
    padding: '0 8px',
  },
  listItem: {
    marginBottom: '8px',
    padding: '12px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'var(--primary-light)',
    },
    '&.Mui-selected': {
      backgroundColor: 'var(--primary-main)',
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'var(--primary-dark)',
      },
    },
  },
  main: {
    flexGrow: 1,
    backgroundColor: 'var(--background-default)',
    padding: '16px',
    display: 'flex',
    height: '100%', // Ensure main content takes full height of container
  },
  mainContent: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden', // Prevents stretching
    backgroundColor: '#ffffff', // White background color for the chat area
  },
  list: {
    overflowY: 'auto',
    flexGrow: 1,
  },
  messageForm: {
    padding: '16px',
  },
  message: {
    display: 'flex',
    marginBottom: '16px',
    alignItems: 'center',
    maxWidth: '600px', // Set max-width for message bubbles
  },
  messageRight: {
    marginLeft: 'auto',
    marginRight: '20px', // Move the right-aligned message bubbles 20px to the left
    justifyContent: 'flex-end', // Align content to the end of the container
  },
  messageLeft: {
    marginRight: 'auto',
    marginLeft: '-52px', // Move the left-aligned message bubbles 40px to the right
    justifyContent: 'flex-start', // Align content to the start of the container
  },
  messageAvatar: {
    backgroundColor: 'var(--primary-main)',
    marginRight: '16px',
    width: '40px',
    height: '40px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    border: '2px solid #fff',
  },
  messageAvatarOther: {
    backgroundColor: '#f50057', // A contrasting color
    marginLeft: '16px',
    width: '40px',
    height: '40px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    border: '2px solid #fff',
  },
  messagePaper: {
    marginLeft: 'auto',
    backgroundColor: 'lightblue',
    marginRight: '0px', // Move the right-aligned message bubbles 20px to the left
    padding: '16px',
    maxWidth: '600px', // Fixed max-width for message bubbles
    wordWrap: 'break-word', // Ensure long words break and wrap within the bubble
  },
  messagePaperOther: {
    padding: '16px',
    backgroundColor: 'lightblue',
    maxWidth: '600px', // Fixed max-width for message bubbles
    wordWrap: 'break-word', // Ensure long words break and wrap within the bubble
  },
  supportMessagePaper: {
    padding: '16px',
    backgroundColor: '#FFF5E1', // Light background color for support messages
    borderLeft: '2px solid orange', // Left border for support messages
    maxWidth: '600px',
    wordWrap: 'break-word',
    borderRadius: '10px', // Adding border radius for support messages
  },
  supportMessageHeader: {
    color: 'gray',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  threadSelected: {
    backgroundColor: 'var(--primary-main)',
  },
  typography: {
    marginBottom: '16px',
  },
  textfield: {
    marginBottom: '16px',
  },
  button: {
    marginBottom: '16px',
  },
  form: {
    padding: '16px',
    backgroundColor: '#f7f7f7', // Light gray background color for the form
    borderRadius: '10px', // Add border radius to the form
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)', // Add subtle shadow for depth
  },
  overflow: {
    flexGrow: 1,
    maxHeight: '70vh', // Adjust the height to ensure it fits within the container
    overflowY: 'auto',
    marginBottom: '16px',
  },
  subject: {
    marginTop: '10px',
    marginLeft: '20px',
  },
};

const Communications = () => {
  const [threads, setThreads] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [communicationType, setCommunicationType] = useState(communicationTypes[0].value);
  const [newSubject, setNewSubject] = useState('');
  const [subjectCharCount, setSubjectCharCount] = useState(0);
  const [selectedThread, setSelectedThread] = useState(null);
  const [showNewMessageForm, setShowNewMessageForm] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [emptyMessageWarning, setEmptyMessageWarning] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [emailVerified, setEmailVerified] = useState(true);
  const [csrfToken, setCsrfToken] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/get-csrf-token`, { withCredentials: true });
        setCsrfToken(response.data.csrf_token);
      } catch (error) {
        console.error('Error fetching CSRF token:', error);
      }
    };

    const fetchThreads = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const userRes = await axios.get(`${API_URL}/api/user_details`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setIsViewer(userRes.data.role === 'Viewer');

          const response = await axios.get(`${API_URL}/api/threads`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const sortedThreads = response.data.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
          setThreads(sortedThreads);
          setLoading(false);
        } catch (err) {
          if (err.response && err.response.data.message === 'Email not verified. Please verify your email to access threads.') {
            setError('You need to verify your email to access this page. Go to the settings page to resolve this.');
          } else {
            setError('Failed to fetch threads');
          }
          setLoading(false);
        }
      } else {
        setError('No access token found');
        setLoading(false);
      }
    };

    fetchCsrfToken();
    fetchThreads();
  }, []);

  const fetchCommunications = async (threadId) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/threads/${threadId}/communications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCommunications(response.data);
      } catch (err) {
        setError('Failed to fetch communications');
      }
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [communications]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const token = localStorage.getItem('accessToken');
    if (token) {
      try {
        const response = await axios.post(
          `${API_URL}/api/threads`,
          {
            type: communicationType,
            subject: newSubject,
          },
          {
            headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
            withCredentials: true,
          }
        );
        const updatedThreads = [...threads, response.data];
        const sortedThreads = updatedThreads.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        setThreads(sortedThreads);
        setSelectedThread(response.data);
        setShowNewMessageForm(false);
        setNewSubject('');
        setSubjectCharCount(0);
      } catch (err) {
        setError('Failed to create new thread');
      }
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!newMessage.trim()) {
      setEmptyMessageWarning(true);
      return;
    }

    const token = localStorage.getItem('accessToken');
    if (token && selectedThread) {
      try {
        const response = await axios.post(
          `${API_URL}/api/threads/${selectedThread.id}/communications`,
          {
            message: newMessage,
          },
          {
            headers: { Authorization: `Bearer ${token}`, 'X-CSRFToken': csrfToken },
            withCredentials: true,
          }
        );
        const updatedCommunications = [...communications, response.data];
        setCommunications(updatedCommunications);
        setNewMessage('');
        setCharCount(0);
        setEmptyMessageWarning(false);

        const updatedThreads = threads.map((thread) => {
          if (thread.id === selectedThread.id) {
            return { ...thread, updated_at: new Date().toISOString() };
          }
          return thread;
        });

        const sortedThreads = updatedThreads.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
        setThreads(sortedThreads);
      } catch (err) {
        setError('Failed to send message');
      }
    }
  };

  const handleThreadSelect = (thread) => {
    setSelectedThread(thread);
    setShowNewMessageForm(false);
    fetchCommunications(thread.id);
  };

  const handleMessageChange = (event) => {
    const message = event.target.value;
    if (message.length <= 2000) {
      setNewMessage(message);
      setCharCount(message.length);
    }
  };

  const handleSubjectChange = (event) => {
    const subject = event.target.value;
    if (subject.length <= 50) {
      setNewSubject(subject);
      setSubjectCharCount(subject.length);
    }
  };

  const getJwtIdentity = () => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      return jwtDecode(token);
    }
    return null;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
    }) + ' ' + date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateSubject = (subject, maxLength) => {
    if (subject.length > maxLength) {
      return subject.slice(0, maxLength) + '...';
    }
    return subject;
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <Box sx={styles.container}>
        <Box component="main" sx={styles.main}>
          <Box sx={styles.mainContent}>
            <Alert severity="error">{error}</Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.container}>
      <Box component="main" sx={styles.main}>
        <Box sx={styles.sidebar}>
          <Typography variant="h6" gutterBottom>
            Chats
          </Typography>
          <TextField variant="outlined" placeholder="Search..." fullWidth sx={styles.textfield} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setShowNewMessageForm(true)}
            sx={styles.button}
            disabled={!emailVerified}
          >
            New Thread
          </Button>
          {!emailVerified && (
            <Alert severity="warning">You need to verify your email to create a new thread.</Alert>
          )}
          <List sx={styles.list}>
            {threads.map((thread) => (
              <ListItem
                button
                key={thread.id}
                onClick={() => handleThreadSelect(thread)}
                selected={selectedThread && selectedThread.id === thread.id}
              >
                <Avatar sx={styles.sidebarThreadAvatar}>
                  {thread.type.charAt(0).toUpperCase()}
                </Avatar>
                <ListItemText
                  primary={truncateSubject(thread.subject, 18)}
                  secondary={`Last message ${formatTimestamp(thread.updated_at)}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
        <Box sx={styles.mainContent}>
          {showNewMessageForm ? (
            <Paper sx={styles.form}>
              <form onSubmit={handleSubmit}>
                <TextField
                  label="Type"
                  value={communicationType}
                  onChange={(e) => setCommunicationType(e.target.value)}
                  select
                  fullWidth
                  sx={styles.textfield}
                >
                  {communicationTypes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Subject"
                  value={newSubject}
                  onChange={handleSubjectChange}
                  fullWidth
                  sx={styles.textfield}
                />
                <Typography variant="caption" color="text.secondary">
                  {subjectCharCount}/50 characters
                </Typography>
                <Button type="submit" variant="contained" color="primary" disabled={!newSubject.trim()}>
                  Create Thread
                </Button>
              </form>
            </Paper>
          ) : selectedThread ? (
            <>
              <Typography variant="h6" gutterBottom sx={styles.subject}>
                {selectedThread.subject}
              </Typography>

              <Box sx={styles.overflow}>
                {communications.map((comm, index) => {
                  const isCurrentUser = comm.user_uuid === getJwtIdentity().user_uuid;
                  const isSupport = comm.user_uuid === '1';
                  const commName = comm.name ? comm.name : 'Unknown User';

                  return (
                    <Box
                      key={index}
                      sx={[
                        styles.message,
                        isSupport ? styles.messageLeft : styles.messageRight,
                      ]}
                      className="communications-message"
                    >
                      {isSupport && (
                        <Avatar sx={styles.messageAvatar}>
                          S
                        </Avatar>
                      )}
                      <Paper
                        elevation={3}
                        sx={
                          isSupport
                            ? styles.supportMessagePaper
                            : isCurrentUser
                            ? styles.messagePaper
                            : styles.messagePaperOther
                        }
                      >
                        {isSupport && (
                          <Typography variant="body2" sx={styles.supportMessageHeader}>
                            Support
                          </Typography>
                        )}
                        <Typography variant="body1">{comm.message}</Typography>
                        <Box display="flex" alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {commName}
                          </Typography>
                          <Box mx={1} />
                          <Typography variant="caption" color="text.secondary">
                            {formatTimestamp(comm.created_at)}
                          </Typography>
                        </Box>
                      </Paper>
                      {!isSupport && (
                        <Avatar sx={styles.messageAvatarOther}>
                          {commName.charAt(0).toUpperCase()}
                        </Avatar>
                      )}
                    </Box>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>
              <Paper sx={styles.messageForm}>
                {isViewer ? (
                  <Alert severity="warning">You are not authorized to send messages on behalf of the organization.</Alert>
                ) : (
                  <form onSubmit={handleSendMessage}>
                    <TextField
                      label="Message"
                      multiline
                      rows={4}
                      value={newMessage}
                      onChange={handleMessageChange}
                      fullWidth
                      sx={styles.textfield}
                      inputProps={{ maxLength: 2000 }}
                    />
                    {emptyMessageWarning && (
                      <Alert severity="warning" onClose={() => setEmptyMessageWarning(false)}>
                        Message cannot be empty.
                      </Alert>
                    )}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Button type="submit" variant="contained" color="primary">
                        Send
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        {charCount}/2000 characters
                      </Typography>
                    </Box>
                  </form>
                )}
              </Paper>
            </>
          ) : (
            <Typography variant="h6" color="text.secondary" sx={styles.subject}>
              Select a chat to view messages or create a new thread.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Communications;
